import React from "react";
import classNames from "classnames";
import { XMarkIcon } from "@heroicons/react/24/outline";

import { CollapsibleSidebarProps } from "./types";

export const CollapsibleSidebar = (props: CollapsibleSidebarProps) => {
  const [isMobile, setIsMobile] = React.useState(false);
  const [touchStart, setTouchStart] = React.useState<number | null>(null);
  const [touchEnd, setTouchEnd] = React.useState<number | null>(null);

  // Check if device is mobile
  React.useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => {
      window.removeEventListener("resize", checkIsMobile);
    };
  }, []);

  React.useLayoutEffect(() => {
    const addFixedToBody = () => {
      // Only prevent body scroll on desktop
      if (!isMobile) {
        document.body.style.position = "fixed";
        document.body.style.top = "0";
        document.body.style.left = "0";
        document.body.style.right = "0";
        document.body.style.bottom = "0";
      } else {
        // On mobile, just prevent scrolling
        document.body.style.overflow = "hidden";
      }
    };

    const removeFixedFromBody = () => {
      if (!isMobile) {
        document.body.style.position = "";
      } else {
        document.body.style.overflow = "";
      }
    };

    if (props.show) {
      addFixedToBody();
    } else {
      removeFixedFromBody();
    }

    return () => {
      removeFixedFromBody();
    };
  }, [props.show, isMobile]);

  React.useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        props.setShow(false);
      }
    };

    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("keydown", handleEsc);
    };
  }, [props]);

  // Touch gesture handlers for mobile
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;

    if (isLeftSwipe && isMobile) {
      props.setShow(false);
    }
  };

  return (
    <>
      {props.show ? (
        <div
          onClick={() => props.setShow(false)}
          className={classNames(
            "top-0 right-0 w-screen h-screen z-50 fixed",
            isMobile ? "bg-opacity-75" : "!bg-opacity-50 bg-black"
          )}
        />
      ) : null}

      <div
        className={classNames(
          "top-0 right-0 bg-white border-l fixed h-full z-50 ease-in-out duration-300 overflow-y-auto",
          props.show ? "translate-x-0 " : "translate-x-full",
          props?.style?.container ?? "",
          isMobile
            ? "w-full p-4"
            : props?.customLayout
              ? ""
              : props?.module === "verifications_module"
                ? "w-[600px] max-w-[35vw] p-[40px]"
                : "min-w-[500px] w-[35vw] p-[40px]"
        )}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="flex items-center">
          {!!props?.title && (
            <div className="flex items-center text-[#343F59] text-xl">
              <p>{props.title}</p>
              {props?.titleContainer}
            </div>
          )}

          <XMarkIcon
            className="ml-auto w-5 h-5 cursor-pointer"
            onClick={() => props.setShow(false)}
          />
        </div>
        <div
          className="flex-1 overflow-y-auto"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          {props.children}
        </div>
      </div>
    </>
  );
};
